import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Stack } from '@chakra-ui/react';
import _ from 'lodash';

import { GoBackButton } from 'components/Button/GoBackButton';
import LoaderButton from 'components/Button/LoaderButton';
import Input from 'components/Input/Input';
import { AppHeading } from 'components/Text/AppHeading';
import { authorize } from 'services/auth';
import { useRegisterStep } from 'services/register-step';
import { PREVIEW_MODE_ERROR_MESSAGE } from 'constants/error-messages';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import WarningMessage from '../Messages/Warning';
import { step } from '../steps';
import { getStatusCode } from '../SignIn/utils';
import type { Recordkeeping as RecordkeepingDetails } from './types';

const Container = styled.form`
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormControls = styled.div`
  width: 100%;
`;

const SelectGroup = styled.div`
  width: 100%;
`;

const SelectLabel = styled.label`
  display: block;
  color: #828282;
  font-size: 12px;
  margin: 0 0 8px 16px;
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid #bdbdbd;
  border-radius: 8px;
  color: #161616;
  font-family: Inter, sans-serif;
  font-size: 16px;
  padding: 16px;
  background: #ffffff;

  &:focus {
    outline: 3px solid #6e56cf;
    outline-offset: 0;
  }
`;

const Recordkeeping = ({
  appType,
  category,
  clientId,
  clientName,
  connectionId,
  currentBrowserSessionKey,
  error,
  handleAuthorizeResponse,
  implementationHasAssistedBenefits,
  previewMode,
  products,
  recordkeeping,
  redirectUri,
  sandbox,
  sessionKey,
  setError,
  setRecordkeeping,
  state,
  prevStep,
}: AuthorizeContextValue) => {
  if (!products) throw new Error('no products');

  const [planId, setPlanId] = useState(recordkeeping?.planId ?? '');
  const [recordkeeper, setRecordkeeper] = useState<
    RecordkeepingDetails['recordkeeper']
  >(recordkeeping?.recordkeeper ?? 'voya');
  const [loading, setLoading] = useState(false);
  const registerStep = useRegisterStep();
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      registerStep({ step: step.RECORDKEEPING });
      mounted.current = true;
    }
  }, [registerStep]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (previewMode) {
      setError({
        message: PREVIEW_MODE_ERROR_MESSAGE,
        omitSessionKey: true,
        status: null,
      });
      setLoading(false);
      return;
    }

    const trimmedPlanId = planId.trim();
    if (!trimmedPlanId) {
      setError({
        message: 'Plan ID is required.',
        omitSessionKey: true,
        status: null,
      });
      setLoading(false);
      return;
    }

    const nextRecordkeeping = {
      planId: trimmedPlanId,
      recordkeeper,
    };

    try {
      registerStep({ step: step.RECORDKEEPING });

      const authorizeResponse = await authorize({
        appType,
        category,
        clientId,
        clientName,
        connectionId,
        currentBrowserSessionKey,
        implementationHasAssistedBenefits,
        products,
        recordkeeping: nextRecordkeeping,
        redirectUri,
        sandbox,
        sessionKey,
        state,
        step: step.RECORDKEEPING,
      });

      setRecordkeeping(nextRecordkeeping);
      handleAuthorizeResponse(authorizeResponse);

      // If we are redirecting, we keep the loading state to avoid double submission.
      // Therefore, we only remove the loading state when there is no redirect.
      if (!('redirect' in authorizeResponse)) {
        setLoading(false);
      }
    } catch (err: any) {
      setLoading(false);
      setError({
        message:
          err.message || 'Unexpected error has occurred. Please try again.',
        formattedMessage: err?.response?.data?.formatted_message,
        status: getStatusCode(err),
        finchCode: _.get(err, 'response.data.finch_code'),
      });
    }
  };

  return (
    <Container onSubmit={onSubmit}>
      <Stack gap="8" width="100%">
        <Header>
          <GoBackButton onClick={prevStep} />
        </Header>
        <AppHeading>Enter recordkeeping details</AppHeading>
        <FormControls>
          <Stack gap="4">
            {error && <WarningMessage>{error.message}</WarningMessage>}
            <SelectGroup>
              <SelectLabel htmlFor="recordkeeping-recordkeeper">
                Recordkeeper
              </SelectLabel>
              <Select
                id="recordkeeping-recordkeeper"
                onChange={(event) =>
                  setRecordkeeper(
                    event.target.value as RecordkeepingDetails['recordkeeper'],
                  )
                }
                value={recordkeeper}
              >
                <option value="voya">Voya</option>
                <option value="empower">Empower</option>
                <option value="transamerica">Transamerica</option>
                <option value="fidelity">Fidelity</option>
              </Select>
            </SelectGroup>
            <Input
              autoFocus
              id="recordkeeping-plan-id"
              invalid={Boolean(error) && !planId.trim()}
              label="Plan ID"
              onChange={(event) => setPlanId(event.target.value)}
              value={planId}
            />
          </Stack>
        </FormControls>
      </Stack>
      <LoaderButton
        disabled={loading}
        isLoading={loading}
        text="Continue"
        type="submit"
      />
    </Container>
  );
};

export default withAuthorizeContext(Recordkeeping);
